import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Phone, Clock, ShieldCheck } from 'lucide-react';
import { useFunnel } from '@/context/FunnelContext';
import {
  trackGenerateLead,
  trackAdsConversion,
  trackPhoneCallConversion,
  registerPhoneConversionSwapForNumber,
  useAdsPhoneSwapVisibility,
} from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { COMPANY } from '@/lib/siteContent';
import { tr } from '@/lib/i18n';

export default function ThankYou() {
  const navigate = useNavigate();
  const { resetAnswers, answers, lang } = useFunnel();
  const t = tr(lang);

  // Snapshot the entry-page phone BEFORE resetAnswers() wipes the funnel state.
  // Priority: (1) entry_phone stashed by the presell/landing page the visitor
  // entered from — so /pa keeps 844-335-8911, /spa keeps 866-524-3722, /dg keeps
  // 833-240-9312, etc. (2) Fallback to the language-appropriate site default.
  const [displayPhone, setDisplayPhone] = useState(() => {
    const fallback = lang === 'es' ? COMPANY.phoneEs : COMPANY.phone;
    return (answers && answers.entry_phone) || fallback;
  });
  const [displayHref, setDisplayHref] = useState(() => {
    const fallback = lang === 'es' ? COMPANY.phoneHrefEs : COMPANY.phoneHref;
    return (answers && answers.entry_phone_href) || fallback;
  });

  useEffect(() => {
    // Read the server-created lead id FIRST — we use it as the stable
    // `transaction_id` so GA4, Google Ads AND the OpenAI Ads pixel all share
    // the same event id as the server-side CAPI mirror (deduped on both ends).
    let leadId = '';
    let enhancedData = null;
    try {
      leadId = sessionStorage.getItem('lp_last_lead_id') || '';
      const raw = sessionStorage.getItem('lp_last_lead_eud');
      if (raw) enhancedData = JSON.parse(raw);
    } catch (e) { /* ignore */ }
    // GA4 lead conversion (reaching Thank-You = a completed lead). No PII sent.
    trackGenerateLead({ lead_type: 'lemon_law_case_review', transaction_id: leadId });
    // Google Ads "Lead Form Submit" conversion — credits the originating ad
    // click. Server-created lead id doubles as `transaction_id` so it matches
    // the offline conversion API upload's `order_id`.
    trackAdsConversion({ value: 1.0, currency: 'USD', transactionId: leadId, enhancedData });
    try {
      sessionStorage.removeItem('lp_last_lead_id');
      sessionStorage.removeItem('lp_last_lead_eud');
    } catch (e) { /* ignore */ }
    // Read entry_phone one more time (in case answers hydrated after mount)
    // then reset the funnel state.
    if (answers?.entry_phone) setDisplayPhone(answers.entry_phone);
    if (answers?.entry_phone_href) setDisplayHref(answers.entry_phone_href);
    resetAnswers();
  }, []);

  // Google Ads "Call from website" phone-number swap for THIS page's number.
  // If the visitor arrived via a Google Ads click, gtag will replace the
  // number on this page with a Google forwarding number for tracking.
  useEffect(() => {
    registerPhoneConversionSwapForNumber(displayPhone);
  }, [displayPhone]);

  // Suppress the Ads phone-swap flicker: keep the number invisible (opacity 0,
  // space reserved) until the DOM swap lands, or a ~900ms safety timeout fires.
  const { ref: phoneRef, visible: phoneVisible } = useAdsPhoneSwapVisibility();
  const phoneStyle = { opacity: phoneVisible ? 1 : 0, transition: 'opacity 150ms ease-in' };

  return (
    <div
      className="min-h-full flex items-center justify-center px-4 pt-[clamp(12px,3vh,32px)] pb-[clamp(88px,15vh,170px)]"
      data-testid="page-thank-you"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-[0_12px_36px_rgba(15,23,42,0.10)] p-[clamp(20px,3vh,40px)] text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="h-9 w-9 text-emerald-500" />
        </div>

        <h1 className="mt-5 font-slab font-bold text-[clamp(1.6rem,3.5vw,2rem)] text-slate-900" data-testid="thank-you-title">
          {t.thankyou.title}
        </h1>

        {/* Primary promise — "we call within 5 min" */}
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-sm font-semibold text-emerald-700" data-testid="thank-you-callback-badge">
          <Clock className="h-4 w-4" />
          <span>{t.thankyou.callBackIn}</span>
        </div>

        <p className="mt-4 text-slate-600 text-[15px] leading-relaxed">
          {t.thankyou.body}
        </p>

        {/* "Or call us right now" — funnels highest-intent leads straight to the phone */}
        <div className="mt-6 border-t border-slate-100 pt-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-testid="thank-you-orcall-label">
            {t.thankyou.orCallUs}
          </p>
          <a
            href={displayHref}
            onClick={trackPhoneCallConversion}
            data-testid="thank-you-call-cta"
            aria-label={`Call The Lemon Pros at ${displayPhone}`}
            className="mt-3 flex flex-col items-center justify-center gap-0.5 rounded-2xl bg-[#EF4444] hover:bg-[#DC2626] text-white px-4 py-4 font-extrabold shadow-md transition-colors"
          >
            <span className="flex items-center gap-2 text-2xl leading-none whitespace-nowrap">
              <Phone className="h-6 w-6" />
              <span ref={phoneRef} style={phoneStyle} data-testid="thank-you-phone-number">
                {displayPhone}
              </span>
            </span>
            <span className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em] text-white/90">
              {t.header?.callNow || 'Call Now'}
            </span>
          </a>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          <span>{t.thankyou.free}</span>
        </div>

        <Button
          onClick={() => navigate(lang === 'es' ? '/sp' : '/')}
          variant="ghost"
          className="mt-5 h-10 w-full rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-50 text-sm font-semibold"
          data-testid="thank-you-home-button"
        >
          {t.thankyou.backHome}
        </Button>
      </div>
    </div>
  );
}
